import { z } from 'zod';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';
import { useInvalidateNotifications } from '@/hooks/use-invalidate-notifications';
import type {
  ApiKeyAdminItem,
  CreateApiKeyResult,
} from '@/features/admin-settings/types/api-key-admin';

// ─── Zod schemas for mutation inputs ──────────────────────────────────────────

export const CreateApiKeySchema = z.object({
  name: z.string().min(1, DICTIONARY.adminSettingsPage.validation.nameRequired),
  wholesalerId: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type CreateApiKeyFormValues = z.infer<typeof CreateApiKeySchema>;

// ─── Fetchers ─────────────────────────────────────────────────────────────────

const fetchApiKeys = async (): Promise<ApiKeyAdminItem[]> => {
  const { data } = await api.get<ApiKeyAdminItem[]>(API_ROUTES.admin.apiKeys);
  return data;
};

const createApiKey = async (payload: CreateApiKeyFormValues): Promise<CreateApiKeyResult> => {
  const { data } = await api.post<CreateApiKeyResult>(API_ROUTES.admin.apiKeys, payload);
  return data;
};

const revokeApiKey = async (id: string): Promise<void> => {
  await api.delete(API_ROUTES.admin.apiKey(id));
};

// ─── Query options factories ───────────────────────────────────────────────────

export const getApiKeysQueryOptions = () =>
  queryOptions({ queryKey: QUERY_KEYS.admin.apiKeys, queryFn: fetchApiKeys });

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useApiKeys = () => useQuery(getApiKeysQueryOptions());

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: createApiKey,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.admin.apiKeys });
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.adminSettingsPage.toast.apiKeyCreated });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.apiKeyCreateError,
        description: getApiErrorMessage(error),
      });
    },
  });
};

export const useRevokeApiKey = () => {
  const queryClient = useQueryClient();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.admin.apiKeys });
      invalidateNotifications();
      toaster.create({ type: 'success', title: DICTIONARY.adminSettingsPage.toast.apiKeyRevoked });
    },
    onError: (error) => {
      toaster.create({
        type: 'error',
        title: DICTIONARY.adminSettingsPage.toast.apiKeyRevokeError,
        description: getApiErrorMessage(error),
      });
    },
  });
};
